import {Close} from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import {toast} from 'sonner';
import {useBulkCloseActions, useBulkCloseStatus} from './useBulkClose';

type BulkCloseProgressToastProps = {
  toastId: string | number;
};

const getTitle = (
  isComplete: boolean,
  failed: boolean,
  succeeded: number,
  skipped: number,
) => {
  if (!isComplete) return 'Closing conversations';
  if (failed) return 'Couldn’t close conversations';
  if (succeeded === 0 && skipped > 0) return 'No conversations closed';
  const label = succeeded === 1 ? 'conversation' : 'conversations';
  return `Closed ${succeeded} ${label}`;
};

const getSubtitle = (
  isComplete: boolean,
  failed: boolean,
  done: number,
  skipped: number,
  total: number,
) => {
  if (!isComplete) return `${done} of ${total}`;
  if (failed) return 'Something went wrong — please try again';
  if (skipped > 0) {
    const label = skipped === 1 ? 'reply' : 'replies';
    return `${skipped} skipped — resident ${label} arrived`;
  }
  return 'All done';
};

export const BulkCloseProgressToast = ({
  toastId,
}: BulkCloseProgressToastProps) => {
  const theme = useTheme();
  const {dismiss} = useBulkCloseActions();
  const {counts, run} = useBulkCloseStatus();

  if (!run) return null;

  const {total, done, succeeded, skipped, isComplete} = counts;
  const failed = run.status === 'FAILED';
  const progress = total === 0 ? 0 : (done / total) * 100;
  const title = getTitle(isComplete, failed, succeeded, skipped);
  const subtitle = getSubtitle(isComplete, failed, done, skipped, total);
  const progressColor = failed
    ? 'error'
    : isComplete && skipped > 0
      ? 'warning'
      : 'primary';

  const handleClose = () => {
    toast.dismiss(toastId);
    dismiss();
  };

  return (
    <Box
      sx={{
        width: 320,
        padding: 1.5,
        borderRadius: theme.tokens.radius.md,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[3],
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
        {!isComplete && <CircularProgress size={18} thickness={5} />}
        <Box sx={{flex: 1, minWidth: 0}}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            {title}
          </Typography>
          <Typography sx={{fontSize: 12, color: theme.palette.text.secondary}}>
            {subtitle}
          </Typography>
        </Box>
        {isComplete && (
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Dismiss"
            data-attr="conversations-bulk-close-toast-dismiss"
          >
            <Close sx={{fontSize: 16}} />
          </IconButton>
        )}
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        color={progressColor}
        sx={{mt: 1.25, height: 6, borderRadius: 3}}
      />
    </Box>
  );
};
